import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageMeta from '../components/PageMeta';
import Reveal from '../components/Reveal';
import ComparisonTable from '../components/ComparisonTable';
import { CheckList, FinalCTA, PageHero, SectionHeading } from '../components/SharedSections';

const { FiPhoneCall, FiVideo, FiMessageSquare, FiFileText, FiCpu, FiBarChart2, FiGitBranch, FiUsers, FiHeadphones, FiLayers, FiDatabase, FiWifi, FiGlobe, FiDollarSign, FiSmartphone, FiShield } = FiIcons;

const pageContent = {
  ccaas: {
    eyebrow: 'Contact Center as a Service',
    title: 'Customer conversations deserve more than a basic call queue.',
    text: 'We help support and sales teams select a contact center platform around real call volume, channels, CRM requirements, and supervisor workflows.',
    meta: 'Vendor-neutral CCaaS consulting for SMB support and sales teams.',
    points: ['Omnichannel voice, SMS, chat, and email', 'Skills-based routing and flexible IVR', 'Supervisor monitoring and workforce analytics', 'CRM and help desk integration planning'],
    cards: [[FiGitBranch, 'Route with context', 'Direct each conversation by skill, customer data, priority, or availability.'], [FiBarChart2, 'Manage performance', 'See queue health, service levels, coaching needs, and customer outcomes.'], [FiDatabase, 'Connect your systems', 'Evaluate integrations against the CRM and service tools your team uses daily.']],
    fitTitle: 'When does an SMB need CCaaS?',
    fitText: 'If customer conversations are a measurable business workflow—not simply calls to an office line—you may need capabilities beyond UCaaS.',
    fitItems: ['Dedicated support or inside-sales teams', 'Multiple queues, departments, or languages', 'Formal service-level or compliance requirements', 'Supervisors who need monitoring and coaching tools'],
    serviceType: 'CCaaS Advisory'
  },
  wireless: {
    eyebrow: 'Multi-carrier business mobility',
    title: 'Every major US carrier. One accountable partner.',
    text: 'Cambia is an authorized broker across AT&T, Verizon, and T-Mobile, plus specialized IoT, M2M, and private-wireless providers — so your plan is built from the real market, not one carrier’s playbook.',
    meta: 'Multi-carrier business wireless brokerage across AT&T, Verizon, and T-Mobile, plus line audits, device strategy, and mobility cost optimization.',
    points: ['Authorized broker access to AT&T, Verizon, and T-Mobile', 'Line-by-line usage and cost audit', 'Carrier and plan comparison across the full market', 'Device lifecycle and upgrade planning'],
    cards: [[FiGlobe, 'Access every major carrier', 'Broker relationships across AT&T, Verizon, and T-Mobile put the entire national market on the table — not one provider’s plan.'], [FiDollarSign, 'Find hidden waste', 'Identify inactive lines, mismatched plans, recurring fees, and unused add-ons.'], [FiShield, 'Control mobile risk', 'Create clearer ownership, inventory, replacement, and device-management practices.']],
    fitTitle: 'Your bill already contains the clues — across every carrier.',
    fitText: 'Wireless costs drift when lines accumulate, usage changes, and plans go unreviewed. Because Cambia brokers AT&T, Verizon, and T-Mobile alongside specialized IoT and private-wireless providers, the review compares your current plan against the entire market, not one company’s next tier up.',
    fitItems: ['Inventory every active line, device, and assigned user', 'Compare actual usage with current plan design', 'Cross-shop AT&T, Verizon, and T-Mobile plus specialized IoT/M2M and private-wireless providers', 'Model device lifecycle, upgrade timing, and total cost before recommending a change'],
    serviceType: 'Business Wireless Advisory'
  }
};

const wirelessCarriers = ['AT&T', 'Verizon', 'T-Mobile', 'IoT & Private Wireless'];

function SolutionPage({ type }) {
  const page = pageContent[type];
  const schema = { '@context': 'https://schema.org', '@type': 'Service', name: page.serviceType, provider: { '@type': 'Organization', name: 'Cambia Digital' }, areaServed: 'New York Tri-State Area' };

  return (
    <>
      <PageMeta title={type === 'ccaas' ? 'CCaaS Advisory' : 'Business Wireless Optimization'} description={page.meta} schema={schema} />
      <PageHero eyebrow={page.eyebrow} title={page.title} text={page.text}>
        <div className="hero-check-card"><h2>What we evaluate</h2><CheckList items={page.points} /></div>
      </PageHero>
      {type === 'wireless' && (
        <section className="trust-strip">
          <p>Authorized broker access across</p>
          <div className="provider-logos">{wirelessCarriers.map((name) => <span key={name}>{name}</span>)}</div>
        </section>
      )}
      <section className="section">
        <Reveal><SectionHeading eyebrow="Built around your operating reality" title={type === 'ccaas' ? 'A better contact center starts with better requirements.' : 'Optimize every line, plan, and device.'} center /></Reveal>
        <div className="card-grid three">
          {page.cards.map(([icon, title, text]) => <Reveal className="solution-card" key={title}><div className="icon-tile"><SafeIcon icon={icon} /></div><h3>{title}</h3><p>{text}</p></Reveal>)}
        </div>
      </section>
      <section className="section tint">
        <div className="split-grid">
          <Reveal><SectionHeading eyebrow="The decision framework" title={page.fitTitle} text={page.fitText} /></Reveal>
          <Reveal className="white-card"><CheckList items={page.fitItems} /></Reveal>
        </div>
      </section>
      {type === 'ccaas' && (
        <section className="section">
          <Reveal><SectionHeading eyebrow="UCaaS or CCaaS?" title="Know when unified communications stops being enough." text="UCaaS serves everyday employee communication. CCaaS adds the routing, channels, controls, and analytics required for structured customer conversations." /></Reveal>
          <Reveal><ComparisonTable condensed /></Reveal>
        </section>
      )}
      <FinalCTA title={type === 'wireless' ? 'Find out where your wireless bill is leaking value.' : 'Build a contact center shortlist around your actual workflow.'} />
    </>
  );
}

export default SolutionPage;